import React, { useState } from 'react';
import { api } from '../utils/api';

type ImportStats = {
  dryRun: boolean;
  rowsProcessed: number;
  skippedEmpty: number;
  missingConstituency: number;
  wardsCreated: number;
  wardRenamed: number;
  wardConstituencyUpdates?: number;
  centersCreated: number;
  centerRenamed: number;
  duplicatesSkipped: number;
  errors: string[];
  missingConstituencySamples?: { row: number; constituency: string; ward: string; center: string }[];
  duplicateSamples?: { type: string; row: number; constituency?: string; ward?: string; wardId?: number; center?: string }[];
  missingWardSamples?: { row: number; constituency: string; ward: string; center: string }[];
};

export default function LocationsImport() {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [result, setResult] = useState<{ message: string; stats: ImportStats } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllConflicts, setShowAllConflicts] = useState(false);
  const [wardDiag, setWardDiag] = useState<any | null>(null);

  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!file) { setError('Please choose an Excel .xlsx file first.'); return; }
    setLoading(true);
    try {
      const { data } = await api.locations.importExcel(file, { dryRun, allowDuplicates, sampleLimit: showAllConflicts ? 'all' : undefined });
      setResult(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const onDiagnoseWards: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    setError(null);
    setWardDiag(null);
    if (!file) { setError('Please choose an Excel .xlsx file first.'); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.locations.diagnoseWards(form);
      setWardDiag(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Ward diagnosis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow">
        <div className="px-4 py-5 border-b sm:px-6">
          <h2 className="text-lg font-semibold text-gray-900">Import Locations from Excel</h2>
          <p className="mt-1 text-sm text-gray-500">Upload a .xlsx with columns: constituency, ward, center. Use Dry Run to preview changes. Note: any dashes ( - , – , — ) in names are replaced with a space automatically.</p>
        </div>

        <form onSubmit={onSubmit} className="p-4 space-y-4 sm:p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Excel file (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full mt-1 text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input id="dry" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="w-4 h-4 border-gray-300 rounded text-primary-600" />
            <label htmlFor="dry" className="text-sm text-gray-700">Dry run (no database changes)</label>
          </div>

          <div className="flex items-center space-x-2">
            <input id="dup" type="checkbox" checked={allowDuplicates} onChange={(e) => setAllowDuplicates(e.target.checked)} className="w-4 h-4 border-gray-300 rounded text-primary-600" />
            <label htmlFor="dup" className="text-sm text-gray-700">Allow duplicate center names within the same ward</label>
          </div>

          <div className="flex items-center space-x-2">
            <input id="allc" type="checkbox" checked={showAllConflicts} onChange={(e) => setShowAllConflicts(e.target.checked)} className="w-4 h-4 border-gray-300 rounded text-primary-600" />
            <label htmlFor="allc" className="text-sm text-gray-700">Return full conflict list (can be large)</label>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="submit"
              disabled={!file || loading}
              className="inline-flex items-center px-4 py-2 text-white rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Uploading…' : dryRun ? 'Preview Import' : 'Run Import'}
            </button>
            <button
              type="button"
              onClick={onDiagnoseWards}
              disabled={!file || loading}
              className="inline-flex items-center px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Analyzing…' : 'Find Unwanted Wards'}
            </button>
            {file && (
              <span className="text-xs text-gray-500">Selected: {file.name}</span>
            )}
          </div>

          {error && (
            <div className="p-3 text-sm text-red-700 rounded bg-red-50">{error}</div>
          )}

          {result && (
            <div className="pt-4 mt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-900">{result.message}</h3>
              <dl className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <div><dt className="text-gray-500">Rows processed</dt><dd className="font-medium">{result.stats.rowsProcessed}</dd></div>
                <div><dt className="text-gray-500">Skipped empty</dt><dd className="font-medium">{result.stats.skippedEmpty}</dd></div>
                <div><dt className="text-gray-500">Missing constituency</dt><dd className="font-medium">{result.stats.missingConstituency}</dd></div>
                <div><dt className="text-gray-500">Wards constituency updated</dt><dd className="font-medium">{result.stats.wardConstituencyUpdates || result.stats.wardRenamed || 0}</dd></div>
                <div><dt className="text-gray-500">Wards not found</dt><dd className="font-medium">{result.stats.missingWardSamples?.length || 0}</dd></div>
                <div><dt className="text-gray-500">Centers created</dt><dd className="font-medium">{result.stats.centersCreated}</dd></div>
                <div><dt className="text-gray-500">Centers renamed</dt><dd className="font-medium">{result.stats.centerRenamed}</dd></div>
                <div><dt className="text-gray-500">Duplicates skipped</dt><dd className="font-medium">{result.stats.duplicatesSkipped}</dd></div>
              </dl>
              {(result.stats.missingConstituencySamples?.length || 0) > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-800">Missing constituencies (first {Math.min(50, result.stats.missingConstituencySamples?.length || 0)})</h4>
                  <div className="mt-2 space-y-1 overflow-auto text-xs text-gray-700 max-h-48">
                    {result.stats.missingConstituencySamples!.map((s, idx) => (
                      <div key={idx}>Row {s.row}: [{s.constituency}] / {s.ward} / {s.center}</div>
                    ))}
                  </div>
                </div>
              )}
              {(result.stats.duplicateSamples?.length || 0) > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-800">Duplicate conflicts (first {Math.min(50, result.stats.duplicateSamples?.length || 0)})</h4>
                  <div className="mt-2 space-y-1 overflow-auto text-xs text-gray-700 max-h-48">
                    {result.stats.duplicateSamples!.map((s, idx) => {
                      const scope = [s.constituency, s.ward].filter(Boolean).join(' / ');
                      return (
                        <div key={idx}>Row {s.row}: {s.type} {scope ? `(${scope})` : ''} {s.center ? `→ ${s.center}` : ''}</div>
                      );
                    })}
                  </div>
                </div>
              )}
              {(result.stats.missingWardSamples?.length || 0) > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-800">Missing wards (not found in DB) (first {Math.min(50, result.stats.missingWardSamples?.length || 0)})</h4>
                  <div className="mt-2 space-y-1 overflow-auto text-xs text-gray-700 max-h-48">
                    {result.stats.missingWardSamples!.map((s, idx) => (
                      <div key={idx}>Row {s.row}: [{s.constituency}] / {s.ward} / {s.center}</div>
                    ))}
                  </div>
                </div>
              )}
              {result.stats.errors?.length ? (
                <div className="p-3 mt-3 text-xs text-yellow-800 rounded bg-yellow-50">
                  {result.stats.errors.join('\n')}
                </div>
              ) : null}
            </div>
          )}

          {wardDiag && (
            <div className="pt-4 mt-8 border-t">
              <h3 className="text-sm font-semibold text-gray-900">Ward Diagnosis</h3>
              <div className="mt-2 text-sm text-gray-700">
                <div>Excel unique wards: <strong>{wardDiag.summary?.excelUniqueWards}</strong></div>
                <div>DB wards: <strong>{wardDiag.summary?.dbWards}</strong></div>
                <div>Matched: <strong>{wardDiag.summary?.matched}</strong></div>
                <div>Extra in DB (not in Excel): <strong>{wardDiag.summary?.extraCount}</strong></div>
                <div>Duplicate groups in DB (normalized): <strong>{wardDiag.summary?.duplicateGroups}</strong></div>
              </div>
              {wardDiag.extra?.length ? (
                <div className="mt-3">
                  <h4 className="text-sm font-semibold text-gray-800">Extra wards in DB (not found in Excel):</h4>
                  <div className="mt-2 overflow-auto text-xs text-gray-800 border rounded max-h-64">
                    <table className="min-w-full">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left">ID</th>
                          <th className="px-2 py-1 text-left">Constituency</th>
                          <th className="px-2 py-1 text-left">Ward</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wardDiag.extra.map((r: any) => (
                          <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                            <td className="px-2 py-1">{r.id}</td>
                            <td className="px-2 py-1">{r.constituencyName}</td>
                            <td className="px-2 py-1">{r.wardName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {wardDiag.dbDuplicates?.length ? (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-800">Duplicate ward groups in DB (same normalized name within same constituency)</h4>
                  <div className="mt-2 space-y-2 overflow-auto text-xs text-gray-800 max-h-64">
                    {wardDiag.dbDuplicates.map((g: any, idx: number) => (
                      <div key={idx} className="p-2 border rounded">
                        <div className="font-medium">{g.items[0]?.constituencyName} / {g.items[0]?.wardName} (x{g.count})</div>
                        <ul className="pl-5 mt-1 list-disc">
                          {g.items.map((i: any) => (
                            <li key={i.id}>#{i.id} — {i.wardName}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
